import cn from "classnames";
import { useEffect } from "react";
import type { PropsWithChildren } from "react";
import { createPortal } from "react-dom";
import { type ActionList, ActionListView } from "./action-list-view";
import { IconX } from "./icons";

/** Full-viewport overlay portaled to body (covers layout chrome). Use for modals that must dim the whole screen. */
export function ModalOverlay({
  children,
  className = "",
  onClose,
  "aria-label": ariaLabel,
  handleEscape = true,
}: PropsWithChildren<{
  className?: string;
  onClose: () => void;
  "aria-label": string;
  handleEscape?: boolean;
}>) {
  useEffect(() => {
    if (!handleEscape) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose, handleEscape]);

  return createPortal(
    <div
      className={cn(
        "fixed inset-0 z-[100] flex items-center justify-center bg-black/50",
        className,
      )}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (
          e.target === e.currentTarget &&
          (e.key === "Enter" || e.key === " ")
        ) {
          if (e.key === " ") e.preventDefault();
          onClose();
        }
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

type ModalProps = PropsWithChildren<{
  isOpen?: boolean;
  className?: string;
  onClose?: () => void;
}>;

export function Modal({
  children,
  className,
  isOpen = false,
  onClose,
}: ModalProps) {
  useEffect(() => {
    if (!isOpen || !onClose) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const classes = cn(
    "bg-white",
    "align-middle max-w-lg w-full p-0",
    "relative inline-block rounded-lg text-left overflow-hidden shadow-xl transform transition-all",
    className,
  );

  if (!isOpen) {
    return null;
  }
  return <div className={classes}>{children}</div>;
}

type ModalHeadingProps = {
  title: string;
  icon?: React.ReactNode;
  titleTrailing?: React.ReactNode;
  description?: string;
  onClose?: () => void;
};
export function ModalHeading({
  title,
  icon,
  titleTrailing,
  description,
  onClose,
}: ModalHeadingProps) {
  const closeButton = onClose ? (
    <button
      type="button"
      className="p-1 hover:bg-gray-100 rounded transition-colors"
      onClick={() => onClose()}
      onKeyDown={() => onClose()}
      aria-label="Close modal"
    >
      <IconX className="w-5 h-5 text-gray-400 hover:text-gray-600" />
    </button>
  ) : null;
  return (
    <div className="m-6 pb-6 mb-0 border-b border-gray-100">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-lg font-bold">{title}</span>
          {titleTrailing}
        </div>
        {onClose && closeButton}
      </div>
      {description ? (
        <p className="mt-2 text-sm font-normal text-gray-700">{description}</p>
      ) : null}
    </div>
  );
}

export function ModalFooter({ actions }: { actions: ActionList }) {
  return (
    <ActionListView
      className="flex items-center gap-2 justify-end px-6 py-4 bg-gray-50"
      actions={actions}
    />
  );
}

export function ModalBody({
  children,
  className,
}: { children: React.ReactNode; className?: string }) {
  return <div className={cn("p-6", className)}>{children}</div>;
}
